import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Button,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as BlockIcon,
  EventAvailable as AvailableIcon,
  DateRange as DateRangeIcon,
  CalendarToday as CalendarTodayIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/fr';

import { useTranslation } from 'react-i18next';
// Services API
import { fetchAvailability } from '../../services/availabilityService';
import api from '../../services/api';
import { AVAILABILITY_STATUS, AVAILABILITY_DESCRIPTIONS } from '../../constants/availabilityStatus';
import {
  availabilityRangesOverlap,
  getAvailabilityDurationInDays,
  getInclusiveAvailabilityEnd,
  isDateWithinAvailabilityRange,
  toExclusiveAvailabilityRange
} from '../../utils/dateUtils';

// Configuration du localisateur
moment.locale('fr');

/**
 * Composant de calendrier interactif pour la gestion des disponibilités
 * Avec navigation mensuelle et sélection de plages de dates
 */
const NewAvailabilityManager = ({ 
  listingId, 
  onAddPeriod,
  onUpdatePeriod,
  onDeletePeriod,
  onSelectPeriod,
  availabilityPeriods: propAvailabilityPeriods,
  onAvailabilityPeriodsChange,
  refreshTrigger,
  bookingMode = 'date_range',
  capacityDefault = null,
  readOnly = false,
  // Plages de dates supprimées localement — persiste entre les re-mounts du calendrier
  excludedDateRanges = []
}) => {
  const { t } = useTranslation();
  // États
  const [localAvailabilityPeriods, setLocalAvailabilityPeriods] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectedPeriodType, setSelectedPeriodType] = useState(AVAILABILITY_STATUS.AVAILABLE);
  // Ne pas afficher le spinner de chargement si les périodes sont fournies via les props
  const [isLoading, setIsLoading] = useState(!Array.isArray(propAvailabilityPeriods));
  const [error, setError] = useState(null);
  const [selectionError, setSelectionError] = useState('');

  // États pour le modal de confirmation de suppression
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [periodToDelete, setPeriodToDelete] = useState(null);

  // Utiliser les périodes du parent en mode création, locales en mode édition
  const availabilityPeriods = listingId ? localAvailabilityPeriods : (propAvailabilityPeriods || []);

  // ── Occupancy from actual reservations (mirrors AvailabilityCalendar.js logic) ──
  // For date_range: Set of 'YYYY-MM-DD' strings that are fully booked
  const [fullyBookedDates, setFullyBookedDates] = useState(new Set());
  // For time_slot/time_slot_capacity: Map of 'YYYY-MM-DD' → [{start_at, end_at, remaining_seats, capacity_total}] for booked/partial slots
  const [bookedSlotsByDay, setBookedSlotsByDay] = useState(new Map());
  // Actual reservations for this listing (for table display when listing_availability has no row)
  const [listingReservations, setListingReservations] = useState([]);

  // Pagination locale infinie (5 lignes)
  const [visibleCount, setVisibleCount] = useState(5);
  // Sélection multiple
  const [selectedIds, setSelectedIds] = useState([]);
  // Ref pour détecter les périodes supprimées entre deux renders et nettoyer fullyBookedDates
  const prevPeriodsRef = useRef([]);
  // Ref stable pour excludedDateRanges — évite de déclencher l'effet d'occupancy
  // à chaque render quand le composant est utilisé sans prop excludedDateRanges
  // (la valeur par défaut [] crée une nouvelle référence à chaque appel).
  const excludedDateRangesRef = useRef(excludedDateRanges);
  excludedDateRangesRef.current = excludedDateRanges;
  const isSlotBasedBookingMode = ['time_slot', 'time_slot_capacity'].includes(bookingMode);

  // Merge listing_availability periods + real reservations not already covered by a listing_availability row
  const sortedAvailabilityPeriods = useMemo(() => {
    // Build a set of reservation_ids that already have a listing_availability row
    const coveredReservationIds = new Set(
      availabilityPeriods.filter(p => p.reservation_id).map(p => p.reservation_id)
    );

    // Build virtual rows for reservations that have NO listing_availability block
    const virtualRows = listingReservations
      .filter(r => !coveredReservationIds.has(r.id))
      .map(r => {
        const resMode = r.booking_mode_snapshot || r.listing?.booking_mode || bookingMode || 'date_range';
        const isSlot = ['time_slot', 'time_slot_capacity'].includes(resMode);
        // For time_slot same-day bookings: start_date === end_date, need to add 1 day for end-exclusive
        let availFrom = r.start_date || (r.start_at ? r.start_at.substring(0, 10) : null);
        let availTo = r.end_date || (r.end_at ? r.end_at.substring(0, 10) : null);
        if (isSlot && availFrom && availTo && availFrom === availTo) {
          // Add 1 day to make end-exclusive cover the day
          availTo = moment(availTo).add(1, 'day').format('YYYY-MM-DD');
        }
        return {
          // Virtual row — no real id in listing_availability
          id: `__res_${r.id}`,
          reservation_id: r.id,
          reservation: r,
          listing_id: r.listing_id,
          available_from: availFrom,
          available_to: availTo,
          start_at: r.start_at || null,
          end_at: r.end_at || null,
          status: 'bloquee',
          reason: null,
          is_reservation_block: true,
          is_virtual: true  // marker so we never try to delete/edit via availability API
        };
      });

    const merged = [...availabilityPeriods, ...virtualRows];

    return merged.sort((left, right) => {
      const leftStart = new Date(left.available_from || left.start_date || left.available_to || left.end_date).getTime();
      const rightStart = new Date(right.available_from || right.start_date || right.available_to || right.end_date).getTime();

      if (rightStart !== leftStart) {
        return rightStart - leftStart;
      }

      const leftEnd = new Date(left.available_to || left.end_date || left.available_from || left.start_date).getTime();
      const rightEnd = new Date(right.available_to || right.end_date || right.available_from || right.start_date).getTime();
      return rightEnd - leftEnd;
    });
  }, [availabilityPeriods, listingReservations]);

  const getPeriodReservation = (period) => period?.reservation || null;

  const getPeriodBookingMode = (period) => {
    const reservation = getPeriodReservation(period);
    // booking_mode_snapshot is set on listing_availability-sourced reservation data
    // listing.booking_mode is available when reservation comes from history/detailed
    return reservation?.booking_mode_snapshot
      || reservation?.listing?.booking_mode
      || bookingMode
      || 'date_range';
  };

  const isReservationLinkedPeriod = (period) => Boolean(period?.reservation_id || getPeriodReservation(period)?.id);

  const isSlotReservationPeriod = (period) => {
    return isReservationLinkedPeriod(period) && ['time_slot', 'time_slot_capacity'].includes(getPeriodBookingMode(period));
  };

  const isPastDate = (date) => date.clone().startOf('day').isBefore(moment().startOf('day'));

  const isPeriodPast = (period) => {
    if (isSlotReservationPeriod(period)) {
      const slotEnd = getPeriodReservation(period)?.end_at || period.end_at;
      return slotEnd ? moment(slotEnd).isSameOrBefore(moment()) : false;
    }

    return moment(period.available_to || period.end_date).isSameOrBefore(moment().startOf('day'));
  };

  const canMutatePeriod = (period) => !readOnly && !isReservationLinkedPeriod(period) && !isPeriodPast(period) && !period?.is_virtual;

  const canDeletePeriod = (period) => canMutatePeriod(period);

  const getPeriodMutationReason = (period) => {
    if (isReservationLinkedPeriod(period)) {
      return t('availabilityManager:immutability.reservation', "Cette période est liée à une réservation et disparaît uniquement en cas d'annulation");
    }

    if (isPeriodPast(period)) {
      return t('availabilityManager:pastPeriodReadOnly', 'Les périodes passées sont en lecture seule');
    }

    return '';
  };

  const getPeriodsForDate = (date) => {
    return sortedAvailabilityPeriods.filter(period =>
      isDateWithinAvailabilityRange(
        date.toDate(),
        period.available_from || period.start_date,
        period.available_to || period.end_date
      )
    );
  };

  const getSlotSummaryForPeriods = (periods) => {
    const labels = periods
      .filter(period => isSlotReservationPeriod(period))
      .map(period => {
        const reservation = getPeriodReservation(period);
        const startAt = reservation?.start_at || period.start_at;
        const endAt = reservation?.end_at || period.end_at;

        if (!startAt || !endAt) {
          return t('availabilityManager:slotReservedFull', 'Créneau réservé');
        }

        const capacityLabel = getPeriodBookingMode(period) === 'time_slot_capacity'
          ? ` • ${reservation?.seats_requested || period.capacity_reserved || 1}/${period.capacity_total || capacityDefault || '?' } pl.`
          : '';

        return `${moment(startAt).format('HH:mm')} - ${moment(endAt).format('HH:mm')}${capacityLabel}`;
      });

    if (labels.length === 0) {
      return '';
    }

    return labels.slice(0, 2).join(' | ');
  };

  const getDateDisplayState = (date) => {
    const periods = getPeriodsForDate(date);
    const fullBlockedPeriod = periods.find(period => period.status === AVAILABILITY_STATUS.BLOCKED && !isSlotReservationPeriod(period));
    const availablePeriod = periods.find(period => period.status === AVAILABILITY_STATUS.AVAILABLE);
    const slotReservationPeriods = periods.filter(period => isSlotReservationPeriod(period));
    const dateStr = date.format('YYYY-MM-DD');

    if (fullBlockedPeriod) {
      return {
        status: AVAILABILITY_STATUS.BLOCKED,
        slotSummary: '',
        tooltip: t('availabilityManager:dateStatus.blocked', 'Période bloquée'),
        periods
      };
    }

    // ── Check real reservation occupancy from booking engine ────────────────
    if (isSlotBasedBookingMode) {
      // For time_slot modes: check slots loaded from booking-engine/slots
      const daySlots = bookedSlotsByDay.get(dateStr) || [];
      const bookedSlots = daySlots.filter(s => !s.available);
      const hasBookedSlots = bookedSlots.length > 0 || slotReservationPeriods.length > 0;
      if (hasBookedSlots) {
        // Build summary from both listing_availability reservation blocks AND real bookings
        const bookedLabels = [
          ...slotReservationPeriods.map(period => {
            const reservation = getPeriodReservation(period);
            const startAt = reservation?.start_at || period.start_at;
            const endAt = reservation?.end_at || period.end_at;
            if (!startAt || !endAt) return t('availabilityManager:slotReservedFull', 'Créneau réservé');
            const capLabel = getPeriodBookingMode(period) === 'time_slot_capacity'
              ? ` • ${reservation?.seats_requested || period.capacity_reserved || 1}/${period.capacity_total || capacityDefault || '?'} pl.`
              : '';
            return `${moment(startAt).format('HH:mm')}-${moment(endAt).format('HH:mm')}${capLabel}`;
          }),
          ...bookedSlots.map(s => {
            const capLabel = s.capacity_total
              ? ` • ${s.capacity_total - (s.remaining_seats || 0)}/${s.capacity_total} pl.`
              : '';
            return `${s.start_at.substring(11, 16)}-${s.end_at.substring(11, 16)}${capLabel}`;
          })
        ];
        const slotSummary = bookedLabels.slice(0, 3).join(' | ');
        return {
          status: 'partial_reserved',
          slotSummary,
          tooltip: `${t('availabilityManager:slotsReservedPrefix', 'Créneaux réservés')}: ${slotSummary}`,
          periods
        };
      }
    } else {
      // date_range mode: check fully booked dates from reservations
      if (fullyBookedDates.has(dateStr)) {
        return {
          status: AVAILABILITY_STATUS.BLOCKED,
          slotSummary: '',
          tooltip: t('availabilityManager:dateStatus.fullyBooked', 'Date entièrement réservée'),
          periods
        };
      }
    }
    // ── End occupancy check ──────────────────────────────────────────────────

    if (availablePeriod) {
      return {
        status: AVAILABILITY_STATUS.AVAILABLE,
        slotSummary: '',
        tooltip: t('availabilityManager:dateStatus.available', 'Période disponible'),
        periods
      };
    }

    return {
      status: null,
      slotSummary: '',
      tooltip: '',
      periods
    };
  };

  const formatPeriodNote = (period) => {
    const reservation = getPeriodReservation(period);

    if (isSlotReservationPeriod(period)) {
      const startAt = reservation?.start_at || period.start_at;
      const endAt = reservation?.end_at || period.end_at;
      const bookingModeForPeriod = getPeriodBookingMode(period);
      const summary = startAt && endAt
        ? `${moment(startAt).format('HH:mm')} - ${moment(endAt).format('HH:mm')}`
        : t('availabilityManager:slotReservedFull', 'Créneau réservé');

      if (bookingModeForPeriod === 'time_slot_capacity') {
        return `${summary} • ${reservation?.seats_requested || period.capacity_reserved || 1}/${period.capacity_total || capacityDefault || '?'} ${t('availabilityManager:seatsBooked', 'places réservées')}`;
      }

      return summary;
    }

    if (isReservationLinkedPeriod(period)) {
      return t('availabilityManager:blockedByReservation', 'Bloquée par une réservation');
    }

    return period.reason || t('availabilityManager:noNote', 'Aucune note');
  };

  const formatPeriodDuration = (period, durationInDays) => {
    if (isSlotReservationPeriod(period)) {
      const reservation = getPeriodReservation(period);
      const startAt = reservation?.start_at || period.start_at;
      const endAt = reservation?.end_at || period.end_at;

      if (startAt && endAt) {
        const durationMinutes = Math.max(moment(endAt).diff(moment(startAt), 'minutes'), 0);
        if (durationMinutes >= 60) {
          const hours = durationMinutes / 60;
          return Number.isInteger(hours) ? `${hours}h` : `${hours.toFixed(1)}h`;
        }
        return `${durationMinutes} min`;
      }
    }

    // use i18next plural support; durationDays_one and _other defined in translations
    // explicit ternary to satisfy manual key requirement
    return durationInDays === 1
      ? t('availabilityManager:durationDays_one', { count: durationInDays })
      : t('availabilityManager:durationDays_other', { count: durationInDays });
  };

  const visiblePeriods = sortedAvailabilityPeriods.slice(0, visibleCount);

  const loadMoreRows = useCallback(() => {
    if (visibleCount < sortedAvailabilityPeriods.length) {
      setVisibleCount(prev => Math.min(prev + 5, sortedAvailabilityPeriods.length));
    }
  }, [visibleCount, sortedAvailabilityPeriods.length]);
  const handleTableScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      loadMoreRows();
    }
  }, [loadMoreRows]);
  // Fonctions de sélection multiple
  const toggleSelect = (id) => {
    const period = sortedAvailabilityPeriods.find(item => String(item.id) === String(id));
    if (!period || !canDeletePeriod(period)) {
      return;
    }
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    const pageIds = visiblePeriods.filter(canDeletePeriod).map(p => p.id);
    setSelectedIds(prev => prev.length === pageIds.length ? [] : pageIds);
  };
  const handleBulkDelete = () => {
    selectedIds.forEach(id => {
      const period = sortedAvailabilityPeriods.find(item => String(item.id) === String(id));
      if (period && canDeletePeriod(period)) {
        onDeletePeriod(id);
      }
    });
    setSelectedIds([]);
  };

  // Fonctions pour gérer le modal de confirmation de suppression
  const handleDeleteClick = (period) => {
    if (!canDeletePeriod(period)) {
      return;
    }
    setPeriodToDelete(period);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (periodToDelete && onDeletePeriod && canDeletePeriod(periodToDelete)) {
      onDeletePeriod(periodToDelete.id);
      setDeleteConfirmOpen(false);
      setPeriodToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPeriodToDelete(null);
  };
  
  useEffect(() => {
    let isMounted = true;

    const syncPeriods = async () => {
      if (Array.isArray(propAvailabilityPeriods)) {
        // Détecter les périodes supprimées depuis le dernier render
        const prevPeriods = prevPeriodsRef.current;
        const removedPeriods = prevPeriods.filter(
          prev => !propAvailabilityPeriods.some(p => String(p.id) === String(prev.id))
        );
        prevPeriodsRef.current = propAvailabilityPeriods;

        setLocalAvailabilityPeriods(propAvailabilityPeriods);

        // Retirer immédiatement les dates/slots des périodes supprimées localement.
        // Sans ça, le calendrier continue d'afficher ces dates en couleur car
        // fullyBookedDates et bookedSlotsByDay sont chargés depuis l'API et ne se
        // rafraîchissent pas après une suppression locale.
        if (removedPeriods.length > 0) {
          // Calculer les dates couvertes par les périodes supprimées
          const removedDateStrings = new Set();
          for (const removed of removedPeriods) {
            const fromStr = removed.available_from || removed.start_date;
            const toStr = removed.available_to || removed.end_date;
            if (!fromStr || !toStr) continue;
            const from = new Date(fromStr + 'T00:00:00Z');
            const to = new Date(toStr + 'T00:00:00Z');
            for (let d = new Date(from); d < to; d.setUTCDate(d.getUTCDate() + 1)) {
              removedDateStrings.add(d.toISOString().split('T')[0]);
            }
          }

          // Nettoyer fullyBookedDates (mode date_range)
          setFullyBookedDates(prev => {
            const next = new Set(prev);
            for (const dateStr of removedDateStrings) next.delete(dateStr);
            return next;
          });

          // Nettoyer bookedSlotsByDay (mode time_slot) : retirer tous les slots
          // dont la date tombe dans la plage supprimée et qui ne sont pas de vraies
          // réservations (available=false uniquement à cause du blocage propriétaire).
          // On supprime entièrement la clé de la Map pour les jours concernés ;
          // l'API sera rechargée si l'utilisateur navigue vers un autre mois.
          setBookedSlotsByDay(prev => {
            const next = new Map(prev);
            for (const dateStr of removedDateStrings) next.delete(dateStr);
            return next;
          });
        }

        setError(null);
        setIsLoading(false);
        return;
      }

      if (!listingId) {
        setLocalAvailabilityPeriods([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const periods = await fetchAvailability(listingId);
        if (!isMounted) return;
        setLocalAvailabilityPeriods(periods || []);
        setError(null);
      } catch (loadError) {
        if (!isMounted) return;
        console.error('Erreur lors du chargement des disponibilités:', loadError);
        setLocalAvailabilityPeriods([]);
        setError(loadError.message || t('availabilityManager:errorLoading', 'Erreur lors du chargement des disponibilités'));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    syncPeriods();

    return () => {
      isMounted = false;
    };
  }, [listingId, propAvailabilityPeriods, refreshTrigger, t]);

  // ── Load reservation occupancy (mirrors AvailabilityCalendar.js) ──────────
  // Fetches actual reservation data (not just listing_availability rows) so the
  // calendar reflects real bookings for all modes:
  //   - date_range / quantity: calls /reservations/fully-booked-dates
  //   - time_slot / time_slot_capacity: calls booking-engine/slots
  useEffect(() => {
    if (!listingId) return;
    let isMounted = true;

    // Calcule le Set de dates à exclure à partir des suppressions locales du parent.
    // Ces plages persistent même après un re-mount du composant car elles viennent
    // du state du parent (CreateListingForm) et non du state local du composant.
    // On lit depuis la ref (toujours à jour) plutôt que depuis la closure pour éviter
    // d'ajouter excludedDateRanges dans le tableau de deps (ce qui causerait une boucle
    // infinie car la valeur par défaut [] est une nouvelle référence à chaque render).
    const buildExcludedDates = () => {
      const excluded = new Set();
      for (const range of excludedDateRangesRef.current) {
        const fromStr = range.available_from || range.start_date;
        const toStr = range.available_to || range.end_date;
        if (!fromStr || !toStr) continue;
        const from = new Date(fromStr + 'T00:00:00Z');
        const to = new Date(toStr + 'T00:00:00Z');
        for (let d = new Date(from); d < to; d.setUTCDate(d.getUTCDate() + 1)) {
          excluded.add(d.toISOString().split('T')[0]);
        }
      }
      return excluded;
    };

    const loadOccupancy = async () => {
      const startOfRange = currentDate.clone().startOf('month').subtract(1, 'month');
      const endOfRange = currentDate.clone().endOf('month').add(1, 'month');
      const startStr = startOfRange.format('YYYY-MM-DD');
      const endStr = endOfRange.format('YYYY-MM-DD');

      if (isSlotBasedBookingMode) {
        // Fetch all slots for the visible range, mark which have capacity left
        try {
          const { data } = await api.get(`/listings/${listingId}/booking-engine/slots`, {
            params: { from: startStr, to: endStr, quantity_requested: 1, seats_requested: 1 }
          });
          if (!isMounted) return;
          const slots = data?.data?.slots || [];
          const excludedDates = buildExcludedDates();
          const byDay = new Map();
          for (const slot of slots) {
            const dayKey = slot.start_at ? slot.start_at.substring(0, 10) : null;
            if (!dayKey) continue;
            // Ignorer les slots dont la date est dans une période supprimée localement
            if (excludedDates.has(dayKey)) continue;
            if (!byDay.has(dayKey)) byDay.set(dayKey, []);
            byDay.get(dayKey).push(slot);
          }
          setBookedSlotsByDay(byDay);
        } catch (err) {
          console.warn('[NewAvailabilityManager] Could not load slot occupancy:', err.message);
          if (isMounted) setBookedSlotsByDay(new Map());
        }
      } else {
        // date_range mode: fetch fully booked dates driven by reservations
        try {
          const response = await api.get('/reservations/fully-booked-dates', {
            params: { listing_id: listingId, start_date: startStr, end_date: endStr }
          });
          if (!isMounted) return;
          const excludedDates = buildExcludedDates();
          const dates = (response.data?.data?.fully_booked_dates || []).filter(
            d => !excludedDates.has(d)
          );
          setFullyBookedDates(new Set(dates));
        } catch (err) {
          console.warn('[NewAvailabilityManager] Could not load fully booked dates:', err.message);
          if (isMounted) setFullyBookedDates(new Set());
        }
      }
    };

    loadOccupancy();
    return () => { isMounted = false; };
  }, [listingId, currentDate, isSlotBasedBookingMode]);

  // ── Load actual reservations to display in the table ─────────────────────
  // For time_slot modes and multi-unit listings, reservations are NOT written to
  // listing_availability. We must fetch them separately and show as virtual rows.
  useEffect(() => {
    if (!listingId) return;
    let isMounted = true;

    const loadReservations = async () => {
      try {
        const response = await api.get('/reservations/my-reservations', {
          params: { role: 'owner', limit: 100, include: 'listing' }
        });
        if (!isMounted) return;
        const all = response.data?.data?.reservations || response.data?.reservations || [];
        // Filter client-side to only this listing's reservations
        const forListing = all.filter(r => r.listing_id === listingId || r.listing?.id === listingId);
        setListingReservations(forListing);
      } catch (err) {
        console.warn('[NewAvailabilityManager] Could not load listing reservations:', err.message);
        if (isMounted) setListingReservations([]);
      }
    };

    loadReservations();
    return () => { isMounted = false; };
  }, [listingId, refreshTrigger]);

  // Navigation dans le calendrier
  const navigateMonth = (direction) => {
    setCurrentDate(prev => prev.clone().add(direction, 'month'));
    setSelectedDates([]);
    setSelectionStart(null);
    setSelectionError('');
  };

  // Générer les jours du mois pour le calendrier
  const generateCalendarDays = () => {
    const startOfMonth = currentDate.clone().startOf('month');
    const endOfMonth = currentDate.clone().endOf('month');
    const startOfWeek = startOfMonth.clone().startOf('week');
    const endOfWeek = endOfMonth.clone().endOf('week');

    const days = [];
    let current = startOfWeek.clone();

    while (current.isSameOrBefore(endOfWeek)) {
      days.push(current.clone());
      current.add(1, 'day');
    }

    return days;
  };

  // Vérifier si une date a une période de disponibilité
  const getDateStatus = (date) => {
    return getDateDisplayState(date).status;
  };

  // Vérifier si une date est disponible pour la sélection
  const isDateAvailableForSelection = (date) => {
    if (isPastDate(date)) {
      return false;
    }

    // Vérifier si la date est déjà occupée par une période existante
    const existingStatus = getDateStatus(date);
    if (existingStatus) {
      return false;
    }
    
    // Vérifier si la date est dans la sélection actuelle
    const dateStr = date.format('YYYY-MM-DD');
    if (selectedDates.includes(dateStr)) {
      return true; // On peut désélectionner
    }
    
    return true;
  };

  // Vérifier les chevauchements avec une période existante (pour l'édition)
  const checkOverlapWithExistingPeriods = (startDate, endDateExclusive, excludePeriodId = null) => {
    for (const period of availabilityPeriods) {
      if (excludePeriodId && period.id === excludePeriodId) {
        continue;
      }

      if (availabilityRangesOverlap(
        startDate,
        endDateExclusive,
        period.available_from || period.start_date,
        period.available_to || period.end_date
      )) {
        return period;
      }
    }

    return null;
  };

  // Gérer la sélection de dates
  const handleDateClick = (date) => {
    if (readOnly) return;
    
    const isCurrentMonth = date.month() === currentDate.month();
    if (!isCurrentMonth) return;

    if (isPastDate(date)) {
      setSelectionError(t('availabilityManager:pastDatesLocked', 'Les dates passées sont verrouillées'));
      return;
    }
    
    const dateStr = date.format('YYYY-MM-DD');
    
    // Effacer les erreurs précédentes
    setSelectionError('');
    
    // Vérifier si la date est déjà occupée
    if (!isDateAvailableForSelection(date)) {
      setSelectionError(t('availabilityManager:dateAlreadyOccupied', 'Cette date est déjà occupée par une période existante'));
      return; // Ne pas permettre la sélection
    }

    if (!selectionStart) {
      // Premier clic - début de sélection
      setSelectionStart(date);
      setSelectedDates([dateStr]);
    } else {
      // Deuxième clic - fin de sélection
      const start = moment.min(selectionStart, date);
      const end = moment.max(selectionStart, date);
      
      // Vérifier que toute la plage est disponible
      const selectedRange = [];
      let current = start.clone();
      let isRangeAvailable = true;
      
      while (current.isSameOrBefore(end, 'day')) {
        const currentDateStr = current.format('YYYY-MM-DD');
        
        // Si une date dans la plage est déjà occupée, annuler la sélection
        if (getDateStatus(current) && !selectedDates.includes(currentDateStr)) {
          isRangeAvailable = false;
          break;
        }
        
        selectedRange.push(currentDateStr);
        current.add(1, 'day');
      }
      
      if (isRangeAvailable) {
        setSelectedDates(selectedRange);
      } else {
        // Réinitialiser la sélection si la plage n'est pas entièrement disponible
        setSelectionError(t('availabilityManager:selectionContainsOccupied', 'La plage sélectionnée contient des dates déjà occupées'));
        setSelectedDates([]);
        setSelectionStart(null);
      }
    }
  };

  // Valider la sélection et créer une nouvelle période
  const validateSelection = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (selectedDates.length === 0) return;

    const startDate = selectedDates[0];
    const endDate = selectedDates[selectedDates.length - 1];
    const exclusiveRange = toExclusiveAvailabilityRange(startDate, endDate);

    const conflictingPeriod = checkOverlapWithExistingPeriods(
      exclusiveRange.available_from,
      exclusiveRange.available_to
    );
    if (conflictingPeriod) {
      const conflictingEnd = getInclusiveAvailabilityEnd(conflictingPeriod.available_to || conflictingPeriod.end_date)
        || conflictingPeriod.available_to
        || conflictingPeriod.end_date;
      setSelectionError(t('availabilityManager:periodOverlapError', `Cette période chevauche avec une période existante (${formatDate(conflictingPeriod.available_from || conflictingPeriod.start_date)} - ${formatDate(conflictingEnd)})`,
        { start: formatDate(conflictingPeriod.available_from || conflictingPeriod.start_date), end: formatDate(conflictingEnd) }));
      return;
    }

    const newPeriod = {
      startDate: exclusiveRange.startDate,
      endDate: exclusiveRange.endDate,
      available_from: exclusiveRange.available_from,
      available_to: exclusiveRange.available_to,
      status: selectedPeriodType,
      reason: '',
      price_per_night: selectedPeriodType === AVAILABILITY_STATUS.AVAILABLE ? 100 : null
    };

    try {
      setSelectionError(''); // Effacer les erreurs précédentes
      await onAddPeriod(newPeriod);
      
      // MODE LOCAL APRÈS CHARGEMENT INITIAL - Pas d'appel backend
      console.log('✅ Période créée localement, gestion par le parent');
      
      // Réinitialiser la sélection
      setSelectedDates([]);
      setSelectionStart(null);
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la période:', error);
      setSelectionError(t('availabilityManager:errorSavingPeriod', 'Erreur lors de la sauvegarde de la période'));
    }
  };

  // Annuler la sélection
  const cancelSelection = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setSelectedDates([]);
    setSelectionStart(null);
    setSelectionError('');
  };

  // Formater une date pour l'affichage
  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY');
  };
  
  // Obtenir le style d'une période selon son statut
  const getStatusInfo = (status) => {
    return AVAILABILITY_DESCRIPTIONS[status] || {
      fr: status,
      color: '#666'
    };
  };

  // Gérer le clic sur une période existante
  const handlePeriodClick = (period) => {
    if (readOnly) return;
    if (!canMutatePeriod(period)) return;

    const inclusiveEndDate = getInclusiveAvailabilityEnd(period.available_to || period.end_date)
      || period.available_to
      || period.end_date;
    
    if (onSelectPeriod) {
      onSelectPeriod({
        id: period.id,
        startDate: new Date(period.available_from || period.start_date),
        endDate: new Date(inclusiveEndDate),
        available_from: period.available_from,
        available_to: period.available_to,
        start_date: period.start_date,
        end_date: period.end_date,
        status: period.status,
        reason: period.reason || '',
        reservation_id: period.reservation_id,
        reservation: period.reservation || null
      });
    }
  };

  // Calculer la durée d'une période en jours
  const calculateDuration = (startDate, endDate) => {
    return getAvailabilityDurationInDays(startDate, endDate);
  };
  
  // Affichage durant le chargement
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="300px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{t('availabilityManager:loading', 'Chargement des disponibilités...')}</Typography>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="fr">
      <Box sx={{ width: '100%' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Typography variant="h5" gutterBottom>
          {listingId ? t('availabilityManager:manageTitle', 'Gestion des disponibilités') : t('availabilityManager:configTitle', 'Configuration des disponibilités')}
        </Typography>
        
        {/* Calendrier navigable */}
        {!readOnly && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <CalendarTodayIcon sx={{ mr: 1 }} />
                {t('availabilityManager:calendarTitle', 'Calendrier des disponibilités')}
              </Typography>
              
              {/* En-tête de navigation */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <IconButton onClick={() => navigateMonth(-1)}>
                  <ChevronLeftIcon />
                </IconButton>
                
                <Typography variant="h6">
                  {currentDate.format('MMMM YYYY')}
                </Typography>
                
                <IconButton onClick={() => navigateMonth(1)}>
                  <ChevronRightIcon />
                </IconButton>
              </Box>
              
              {/* Calendrier */}
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                {/* En-têtes des jours */}
                <Grid container>
                  {[t('availabilityManager:dayMon', 'Lun'), t('availabilityManager:dayTue', 'Mar'), t('availabilityManager:dayWed', 'Mer'), t('availabilityManager:dayThu', 'Jeu'), t('availabilityManager:dayFri', 'Ven'), t('availabilityManager:daySat', 'Sam'), t('availabilityManager:daySun', 'Dim')].map(day => (
                    <Grid item xs={12/7} key={day}>
                      <Box 
                        sx={{ 
                          textAlign: 'center', 
                          py: 1, 
                          fontWeight: 'bold',
                          backgroundColor: 'grey.100',
                          borderRight: 1,
                          borderColor: 'divider'
                        }}
                      >
                        {day}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                
                {/* Grille des jours */}
                <Grid container>
                  {generateCalendarDays().map((day, index) => {
                    const isCurrentMonth = day.month() === currentDate.month();
                    const isToday = day.isSame(moment(), 'day');
                    const dayState = getDateDisplayState(day);
                    const dateStatus = dayState.status;
                    const isSelected = selectedDates.includes(day.format('YYYY-MM-DD'));
                    const isAvailableForSelection = isDateAvailableForSelection(day);
                    const isPastDay = isPastDate(day);
                    
                    let bgColor = 'white';
                    let textColor = isCurrentMonth ? 'text.primary' : 'text.disabled';
                    let cursor = 'default';
                    let opacity = isCurrentMonth ? 1 : 0.5;
                    
                    if (isSelected) {
                      bgColor = 'primary.light';
                      textColor = 'white';
                      cursor = 'pointer';
                    } else if (isPastDay) {
                      bgColor = 'grey.200';
                      textColor = 'text.disabled';
                      cursor = 'not-allowed';
                      opacity = 0.75;
                    } else if (dateStatus === AVAILABILITY_STATUS.AVAILABLE) {
                      bgColor = 'success.light';
                      textColor = 'success.contrastText';
                      cursor = 'not-allowed';
                      opacity = 0.7;
                    } else if (dateStatus === AVAILABILITY_STATUS.BLOCKED) {
                      bgColor = 'error.light';
                      textColor = 'error.contrastText';
                      cursor = 'not-allowed';
                      opacity = 0.7;
                    } else if (dateStatus === 'partial_reserved') {
                      bgColor = '#fff4d6';
                      textColor = '#8a4b00';
                      cursor = 'not-allowed';
                      opacity = 0.95;
                    } else if (isCurrentMonth && !readOnly) {
                      cursor = 'pointer';
                    }
                    
                    return (
                      <Grid item xs={12/7} key={index}>
                        <Tooltip title={dayState.tooltip || ''} placement="top" arrow>
                        <Box
                          onClick={() => handleDateClick(day)}
                          sx={{
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: bgColor,
                            color: textColor,
                            borderRight: 1,
                            borderBottom: 1,
                            borderColor: 'divider',
                            cursor: cursor,
                            opacity: opacity,
                            fontWeight: isToday ? 'bold' : 'normal',
                            position: 'relative',
                            '&:hover': !readOnly && isCurrentMonth && isAvailableForSelection ? {
                              backgroundColor: isSelected ? 'primary.main' : 'grey.100'
                            } : {}
                          }}
                        >
                          {day.date()}
                          {/* Indicateur visuel pour les dates occupées */}
                          {dateStatus && !isSelected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 2,
                                right: 2,
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                backgroundColor: dateStatus === AVAILABILITY_STATUS.AVAILABLE
                                  ? 'success.main'
                                  : dateStatus === 'partial_reserved'
                                    ? 'warning.main'
                                    : 'error.main'
                              }}
                            />
                          )}
                          {dateStatus === 'partial_reserved' && !isSelected && (
                            <Typography
                              variant="caption"
                              sx={{
                                position: 'absolute',
                                bottom: 2,
                                left: 2,
                                right: 2,
                                fontSize: '0.5rem',
                                lineHeight: 1,
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: '#8a4b00'
                              }}
                            >
                              {dayState.slotSummary || 'Créneau réservé'}
                            </Typography>
                          )}
                        </Box>
                        </Tooltip>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
              
              {/* Légende */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: 'success.light', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.available', 'Disponible')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: 'error.light', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.blocked', 'Bloqué')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: '#fff4d6', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.slotReserved', 'Créneau réservé')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: 'grey.200', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.past', 'Passé')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: 'primary.light', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.selected', 'Sélectionné')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 16, height: 16, backgroundColor: 'white', border: 1, borderColor: 'divider' }} />
                  <Typography variant="caption">{t('availabilityManager:legend.free', 'Libre')}</Typography>
                </Box>
              </Box>
              
              {/* Message d'erreur de sélection */}
              {selectionError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {selectionError}
                </Alert>
              )}
              
              {/* Contrôles de sélection */}
              {selectedDates.length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {t('availabilityManager:selectedDays', { count: selectedDates.length, defaultValue: '{{count}} jour(s) sélectionné(s)' })}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <FormControl size="small">
                      <Select
                        value={selectedPeriodType}
                        onChange={(e) => setSelectedPeriodType(e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value={AVAILABILITY_STATUS.AVAILABLE}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <AvailableIcon sx={{ mr: 1, color: 'success.main' }} />
                            {t('availabilityManager:available', 'Disponible')}
                          </Box>
                        </MenuItem>
                        <MenuItem value={AVAILABILITY_STATUS.BLOCKED}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <BlockIcon sx={{ mr: 1, color: 'error.main' }} />
                            {t('availabilityManager:blocked', 'Bloqué')}
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                    
                    <Button 
                      type="button"
                      variant="contained" 
                      size="small"
                      onClick={(event) => validateSelection(event)}
                      startIcon={<AddIcon />}
                    >
                      {t('availabilityManager:validate', 'Valider')}
                    </Button>
                    
                    <Button 
                      type="button"
                      variant="outlined" 
                      size="small"
                      onClick={(event) => cancelSelection(event)}
                    >
                      {t('availabilityManager:cancel', 'Annuler')}
                    </Button>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        )}
        
        {/* Liste des périodes existantes */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DateRangeIcon sx={{ mr: 1 }} />
              {t('availabilityManager:periodsTitle', 'Périodes de disponibilité')} ({availabilityPeriods.length})
            </Typography>
            
            {availabilityPeriods.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarTodayIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  {listingId ? t('availabilityManager:noPeriodsDefined', 'Aucune période de disponibilité définie') : t('availabilityManager:noPeriodsConfigured', 'Aucune période configurée pour ce listing')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('availabilityManager:useCalendarToAdd', 'Utilisez le calendrier ci-dessus pour ajouter des périodes')}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Bouton suppression en lot */}
                {selectedIds.length > 0 && (
                  <Box sx={{ mb: 1, textAlign: 'right' }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={handleBulkDelete}
                    >
                      {t('availabilityManager:bulkDelete', { count: selectedIds.length, defaultValue: 'Supprimer {{count}} sélection' })}{selectedIds.length > 1 ? '' : ''}
                    </Button>
                  </Box>
                )}
                <Box sx={{ maxHeight: 400, overflowY: 'auto' }} onScroll={handleTableScroll}>
                <TableContainer>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            onClick={(e) => e.stopPropagation()}
                            checked={visiblePeriods.filter(canDeletePeriod).length > 0 && selectedIds.length === visiblePeriods.filter(canDeletePeriod).length}
                            indeterminate={selectedIds.length > 0 && selectedIds.length < visiblePeriods.filter(canDeletePeriod).length}
                            onChange={toggleSelectAll}
                            color="primary"
                            size="small"
                            disabled={visiblePeriods.filter(canDeletePeriod).length === 0}
                          />
                        </TableCell>
                        <TableCell>{t('availabilityManager:status', 'Statut')}</TableCell>
                        <TableCell>{t('availabilityManager:startDate', 'Date de début')}</TableCell>
                        <TableCell>{t('availabilityManager:endDate', 'Date de fin')}</TableCell>
                        <TableCell>{t('availabilityManager:duration', 'Durée')}</TableCell>
                        <TableCell>{t('availabilityManager:note', 'Note')}</TableCell>
                        {!readOnly && <TableCell>{t('availabilityManager:actions', 'Actions')}</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visiblePeriods.map((period, index) => {
                        const statusInfo = getStatusInfo(period.status);
                        const reservation = getPeriodReservation(period);
                        const isImmutable = !canMutatePeriod(period);
                        const isPastPeriod = isPeriodPast(period);
                        // Convert end-exclusive available_to -> inclusive display date.
                        // Guard: if available_to <= available_from (malformed data: same-day slot
                        // reservation stored without +1 end-exclusive offset), show available_from
                        // instead of producing a date before the start.
                        const rawStartDate = period.available_from || period.start_date;
                        const rawEndDate = period.available_to || period.end_date;
                        const inclusiveEnd = (isSlotReservationPeriod(period) && reservation?.end_at)
                          ? moment(reservation.end_at).format('YYYY-MM-DD')
                          : (getInclusiveAvailabilityEnd(rawEndDate) || rawEndDate);
                        const displayEndDate = (rawStartDate && inclusiveEnd && inclusiveEnd < rawStartDate)
                          ? rawStartDate
                          : inclusiveEnd;
                        const duration = calculateDuration(
                          period.available_from || period.start_date, 
                          period.available_to || period.end_date
                        );
                        const bookingModeForPeriod = getPeriodBookingMode(period);
                        const chipLabel = isReservationLinkedPeriod(period)
                          ? bookingModeForPeriod === 'time_slot_capacity'
                            ? t('availabilityManager:reservationSlotCapacity', 'Réservation créneau partagé')
                            : bookingModeForPeriod === 'time_slot'
                              ? t('availabilityManager:reservationSlot', 'Réservation créneau')
                              : t('availabilityManager:reservation', 'Réservation')
                          : t(`availabilityStatus.${period.status}`, statusInfo.fr);
                        
                        return (
                          <TableRow 
                            key={period.id || index}
                            hover={!isImmutable}
                            sx={{ 
                              cursor: isImmutable ? 'default' : 'pointer',
                              backgroundColor: isPastPeriod ? 'grey.100' : 'inherit',
                              opacity: isPastPeriod ? 0.72 : 1,
                              '&:hover': {
                                backgroundColor: isImmutable ? (isPastPeriod ? 'grey.100' : 'inherit') : 'action.hover'
                              }
                            }}
                            onClick={() => !isImmutable && handlePeriodClick(period)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                onClick={(e) => e.stopPropagation()}
                                checked={selectedIds.includes(period.id)}
                                onChange={() => toggleSelect(period.id)}
                                color="primary"
                                size="small"
                                disabled={!canDeletePeriod(period)}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={chipLabel}
                                size="small" 
                                sx={{ 
                                  backgroundColor: isReservationLinkedPeriod(period) ? 'rgba(255, 152, 0, 0.18)' : `${statusInfo.color}20`, 
                                  color: isReservationLinkedPeriod(period) ? '#8a4b00' : statusInfo.color,
                                  fontWeight: 'bold'
                                }} 
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(period.available_from || period.start_date)}</Typography>
                              {isSlotReservationPeriod(period) && reservation?.start_at && (
                                <Typography variant="caption" color="text.secondary">{moment(reservation.start_at).format('HH:mm')}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{formatDate(displayEndDate)}</Typography>
                              {isSlotReservationPeriod(period) && reservation?.end_at && (
                                <Typography variant="caption" color="text.secondary">{moment(reservation.end_at).format('HH:mm')}</Typography>
                              )}
                            </TableCell>
                            <TableCell>{formatPeriodDuration(period, duration)}</TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontStyle: period.reason ? 'normal' : 'italic' }}>
                                {formatPeriodNote(period)}
                              </Typography>
                              {isReservationLinkedPeriod(period) && reservation?.status && (
                                <Typography variant="caption" color="text.secondary">
                                  Réservation {reservation.status}
                                </Typography>
                              )}
                            </TableCell>
                            {!readOnly && (
                              <TableCell>
                                <Tooltip title={isImmutable ? getPeriodMutationReason(period) : t('availabilityManager:edit', 'Modifier')}>
                                  <span>
                                    <IconButton 
                                      size="small"
                                      disabled={isImmutable}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePeriodClick(period);
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {onDeletePeriod && (
                                  <Tooltip title={canDeletePeriod(period) ? t('availabilityManager:delete', 'Supprimer') : getPeriodMutationReason(period)}>
                                    <span>
                                      <IconButton 
                                        size="small"
                                        color="error"
                                        disabled={!canDeletePeriod(period)}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteClick(period);
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                </Box>
                {visibleCount < sortedAvailabilityPeriods.length && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Button variant="contained" size="small" onClick={loadMoreRows}>
                      {t('availabilityManager:loadMore', 'Voir plus')}
                    </Button>
                  </Box>
                )}
                
                {/* Bouton de suppression en masse */}
                {!readOnly && selectedIds.length > 0 && (
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button 
                      variant="outlined" 
                      color="error"
                      size="small"
                      onClick={handleBulkDelete}
                      startIcon={<DeleteIcon />}
                    >
                      {t('availabilityManager:deleteSelection', 'Supprimer la sélection')}
                    </Button>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>
        
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            {readOnly 
              ? t('availabilityManager:readOnly', 'Mode lecture seule - modification non autorisée')
              : t('availabilityManager:footerText', 'Cliquez sur une ligne modifiable pour éditer. Les réservations et les périodes passées sont verrouillées.')
            }
          </Typography>
        </Box>

        {/* Modal de confirmation de suppression personnalisé */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={handleCancelDelete}
          PaperProps={{
            sx: {
              borderRadius: 3,
              minWidth: '400px',
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              border: '1px solid rgba(255, 0, 0, 0.1)'
            }
          }}
        >
          <DialogTitle
            sx={{
              backgroundColor: 'error.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontWeight: 600
            }}
          >
            <DeleteIcon />
            {t('availabilityManager:confirmDeleteTitle', 'Confirmer la suppression')}
          </DialogTitle>
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <DialogContentText
              sx={{
                fontSize: '1rem',
                color: 'text.primary',
                mb: 2
              }}
            >
              {t('availabilityManager:confirmDeleteMessage', 'Êtes-vous sûr de vouloir supprimer cette période de disponibilité ?')}
            </DialogContentText>
            {periodToDelete && (
              <>
              {(() => {
                const rawStartDateDel = periodToDelete.available_from || periodToDelete.start_date;
                const rawEndDateDel = periodToDelete.available_to || periodToDelete.end_date;
                const inclusiveEndDel = getInclusiveAvailabilityEnd(rawEndDateDel) || rawEndDateDel;
                const displayEndDate = (rawStartDateDel && inclusiveEndDel && inclusiveEndDel < rawStartDateDel)
                  ? rawStartDateDel
                  : inclusiveEndDel;
                return (
              <Box
                sx={{
                  backgroundColor: 'grey.50',
                  borderRadius: 2,
                  p: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                  {t('availabilityManager:periodToDelete', 'Période à supprimer :')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Du {moment(periodToDelete.available_from || periodToDelete.start_date).format('DD/MM/YYYY')} 
                  au {moment(displayEndDate).format('DD/MM/YYYY')}
                </Typography>
                <Chip 
                  size="small"
                  label={t(`availabilityStatus.${periodToDelete.status}`, periodToDelete.status)}
                  sx={{ 
                    mt: 1,
                    backgroundColor: `${AVAILABILITY_DESCRIPTIONS[periodToDelete.status]?.color || '#666'}20`,
                    color: AVAILABILITY_DESCRIPTIONS[periodToDelete.status]?.color || '#666'
                  }}
                />
              </Box>
                );
              })()}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
            <Button
              onClick={handleCancelDelete}
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500
              }}
            >
              {t('common.cancel', 'Annuler')}
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
              }}
            >
              {t('availabilityManager:delete', 'Supprimer')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NewAvailabilityManager;
